import React, { useLayoutEffect, useMemo, useState } from 'react';
import TopicProgressCard from './TopicProgressCard';
import {
  ETaskStatus,
  useKeywordAnalysisDetail,
} from '../../hook/useTopicAnalysisFlow';
import { AnalysisSummaryTypeTab } from '../../types';
// import { AnalysisChartsConfig } from '../../constants';
import { TGptErrorType } from './GPTSummaryContent';
import { useInjectionContext } from '../../provider';
import { AnalysisChartsConfig } from '../../constants';
import useReportCreateSuccess from '../../../hooks/useReportCreateSuccess';

export type TChildrenPropsType = {
  summaryId: string | null;
  keywordGptLoading: boolean;
  filter: any;
  value: AnalysisSummaryTypeTab;
  setValue: React.Dispatch<React.SetStateAction<AnalysisSummaryTypeTab>>;
  tabFilter: AnalysisSummaryTypeTab[];
  chartConfig: any;
  keywordGptError?: TGptErrorType;
  isTopicAnalysis: boolean;
  topicSelect: string[];
  setTopicSelect: React.Dispatch<React.SetStateAction<any[]>>;
  wholeGameInfo: any;
  gameInfo: any;
  topicSelectFilter: any;
  updateGptParams: any;
};

export default function LoadingCard({
  children,
  filterValue, 
}: {
  children: (value: TChildrenPropsType) => JSX.Element;
  filterValue: any; 
}) {
  const {
    setLoading,
    gameInfo,
    wholeGameInfo,
    taskId,
    dataType,
    versionId,
    successTagged,
    isPreviews,
  } = useInjectionContext();

  const {
    requestAnalysisDetail,
    summaryId,
    topicFilter,
    summaryStatus,
    loading,
    isTopicAnalysis,
    showChildren,
    getSummaryIsSuccess,
    updateGptParams,
    taskStatus,
  } = useKeywordAnalysisDetail({
    timer: 10 * 1000,
  });

  const [value, setValue] = useState<AnalysisSummaryTypeTab>('total');
  const chartConfig = useMemo(
    () => AnalysisChartsConfig?.[value] || [],
    [value],
  );

  /**
   * 开始加载, 分两种情况
   * secondaryInvocation 为true，则表示是第二次调用，则不进行请求，直接展示children
   * @param isTopicBack
   */
  async function beginLoadingTopicAnalysis(secondaryInvocation: boolean) {
    const params: any = { task_id: taskId, data_type: dataType, ...gameInfo };
    if (versionId) {
      params.version_id = versionId;
    }
    requestAnalysisDetail(params, {
      callbackStop: (data: any) => {
        const {
          last_keyword_analysis_task_queue_id,
          keyword_analysis_task_category,
        } = data || {};
        // 如果是话题，并且第一次进入，那么就判断这两个id是否生成了，如果生成，就进入下一步
        if (
          keyword_analysis_task_category === 'topic' &&
          !secondaryInvocation
        ) {
          return last_keyword_analysis_task_queue_id;
        }
        // 如果是非新数据类型，并且存在任务ID或摘要状态不为0，则停止请求
        return getSummaryIsSuccess(data);
      },
      callbackShowChildren: (data: any) => {
        if (!data) return false;
        if (successTagged) return true;
        if (
          data?.keyword_analysis_task_category === 'topic' &&
          !secondaryInvocation
        ) {
          return false;
        }
        return true;
      },
      showChildrenCallback: () => {
        setLoading(false);
      },
      timer: (secondaryInvocation ? 30 : 3) * 1000,
    });
  }

  useLayoutEffect(() => {
    if (taskId) {
      beginLoadingTopicAnalysis(false);
    }
  }, [taskId, dataType, versionId]);

  // const [value, setValue] = useState<AnalysisSummaryTypeTab>('total');
  const [topicSelect, setTopicSelect] = useState<string[]>([]);
  const tabFilter: AnalysisSummaryTypeTab[] =
    wholeGameInfo?.entity_type === 'pc'
      ? ['app store', 'google play']
      : ['steam'];

  // Topic 分析：数据打标板块出现时自动滚动（预览模式不滚动）
  const { reportRef: topicProgressRef } = useReportCreateSuccess({
    trigger: isTopicAnalysis && !isPreviews,
    delay: 300,
    maxRetries: 8,
  });

  // 非 Topic 分析（Keyword / URL）：内容区块渲染完成后自动滚动（预览模式不滚动）
  const { reportRef: contentRef } = useReportCreateSuccess({
    trigger: showChildren === true && !isTopicAnalysis && !isPreviews,
  });

  const filter = useMemo(() => {
    // 如果过滤值为空，则不进行设置数据进行请求
    if (!filterValue) return null;
    if (!showChildren) return null;
    let data = filterValue;
    if (isTopicAnalysis) {
      // 如果不是TopicAnalysis，则不进行设置数据进行请求
      // if (!filterId) return null;

      data = {
        ...filterValue,
        topic_analysis_task_id: taskId,
        topic_analysis_topics: topicSelect,
      };
    }

    const {
      channel_comments,
      channel_news,
      channel_securitys,
      channel_social,
      ...otherFilterInfo
    } = data;

    if (['google play', 'app store', 'steam'].includes(value)) {
      return {
        ...otherFilterInfo,
        channel: [value],
        channel_type: 'comments',
      };
    }

    if (value === 'community') {
      return {
        ...otherFilterInfo,
        channel: channel_social,
        channel_type: 'social',
      };
    }

    return data;
  }, [filterValue, value, isTopicAnalysis, showChildren, topicSelect]);

  const topicSelectFilter = useMemo(() => {
    return {
      task_id: taskId,
      ...gameInfo,
    };
  }, [taskId]);

  const newSummaryId = useMemo(() => {
    if (topicSelect.length === 0) {
      return summaryId;
    }

    return null;
  }, [summaryId, topicSelect]);

  return (
    <div className="dbo-loading-card-wrapper">
      {isTopicAnalysis && (
        <div ref={topicProgressRef}>
          <TopicProgressCard
            wholeGameInfo={wholeGameInfo}
            successTagged={successTagged}
            requestInfo={async () => {
              if (successTagged) return;
              await beginLoadingTopicAnalysis(true);
            }}
            defaultExpanded={taskStatus !== ETaskStatus.COMPLETED}
            isStop={taskStatus === ETaskStatus.STOP}
            filter={topicFilter}
          />
        </div>
      )}
      {showChildren && (
        <div ref={contentRef}>
          {children({
            summaryId: newSummaryId,
            keywordGptLoading: loading,
            value,
            setValue,
            filter,
            tabFilter,
            chartConfig,
            keywordGptError: summaryStatus,
            isTopicAnalysis,
            topicSelect,
            setTopicSelect,
            gameInfo,
            wholeGameInfo,
            topicSelectFilter,
            updateGptParams,
          })}
        </div>
      )}
    </div>
  );
}
